// @flow

import * as React from 'react';

import { useField } from 'react-final-form';

import {
  ChoiceField,
  ChoiceFieldGroup,
  useBasicValidators,
} from '@gandi/form/FinalForm';
import { useTranslator } from '@gandi/translate';
import { Alert, Base, Block, BlockContent, BlockHeading } from '@gandi/ui';

import {
  CERT_AUTO_DCV_VALID,
  CERT_CN_INPUT_NAME,
  CERT_DCV_METHOD_INPUT_NAME,
  CERT_TYPE_PROTECT_INPUT_NAME,
  choiceGroupProps,
} from '../../consts';

export function DCVMethodOption(): React.MixedElement {
  const { __ } = useTranslator();
  const {
    input: { value: protect },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_TYPE_PROTECT_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: cn },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_CN_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: dcvMethod },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_DCV_METHOD_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: autoDcvValid },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_AUTO_DCV_VALID, { subscription: { value: true } });

  const { required } = useBasicValidators();

  const items = [
    {
      value: 'dns',
      label: __('DNS record'),
    },
    {
      value: 'email',
      label: __('Email'),
    },
  ];

  if (autoDcvValid) {
    items.unshift({
      value: 'auto',
      label: __('Auto'),
    });
  }

  if (protect !== 'wildcard' && !cn.startsWith('*.')) {
    items.push({
      value: 'file',
      label: __('File'),
    });
  }

  const helpMap = {
    auto: __(
      'Since your certificate will be used with a domain name registered at Gandi, its validation will be done automatically.',
    ),
    dns: __(
      'This method requires that you to add an entry in the DNS records of all the domains you want to secure. The exact record will be displayed in your Certificates admin on Gandi. Validation by DNS record implies that you have access to the DNS zone file of the domains (whether or not at Gandi), and can add a CNAME record to it.',
    ),
    email: __(
      'This validation method requires that you have a specific email address available for each domain to be validated. This email address must be created with the user admin@ (ex.: if you want to validate the domain example.com, you must create the email address admin@example.com).',
    ),
    file: __(
      "To use this validation method, you will have to place a .txt in the root directory of your website. If you don't have access to this directory, please choose another validation method. This file will be available in your Certificates admin on Gandi.",
    ),
  };

  const fileWarning = __(
    'Your .txt file needs to be available through HTTP. The CA will verify the file within 1 hour of the launch of the validation process.',
  );

  const help = helpMap[dcvMethod];
  const isFile = dcvMethod === 'file';

  return (
    <>
      <Block>
        <BlockHeading>{__('Choose your validation method')}</BlockHeading>
        <BlockContent>
          <Base marginBottom="small">
            <ChoiceFieldGroup
              name={CERT_DCV_METHOD_INPUT_NAME}
              validate={required}
              choiceGroupProps={choiceGroupProps}
            >
              {items.map(({ label, value }) => (
                <ChoiceField
                  key={value}
                  name={CERT_DCV_METHOD_INPUT_NAME}
                  value={value}
                  label={label}
                />
              ))}
            </ChoiceFieldGroup>
          </Base>
          <Base tagName="p" margin="none">
            {help}
          </Base>
        </BlockContent>
      </Block>
      {isFile && (
        <Alert intent="warning" message={fileWarning} marginTop="small" />
      )}
    </>
  );
}
